/**
 * Fractional ordering for kanban columns: every item has a `position` number
 * and moving an item only rewrites that item, placed halfway between its new
 * neighbors. The order survives reloads because it is stored in the database.
 */

const GAP = 1;

export interface Positioned {
  id: string | number;
  position: number;
}

export const sortByPosition = <T extends Positioned>(items: T[]): T[] =>
  [...items].sort(
    (a, b) =>
      a.position - b.position || String(a.id).localeCompare(String(b.id)),
  );

/**
 * Position for an item dropped at `index` in a column that does not contain it
 * anymore (`items` sorted by position, without the moved item).
 */
export const getPositionAt = (items: Positioned[], index: number): number => {
  if (items.length === 0) return GAP;
  if (index <= 0) return items[0].position - GAP;
  if (index >= items.length) return items[items.length - 1].position + GAP;
  return (items[index - 1].position + items[index].position) / 2;
};

/**
 * Position for an item created where its column is not loaded (e.g. a task
 * created from a card): newer items get smaller positions, so they show first.
 */
export const newItemPosition = (now: number = Date.now()): number =>
  -now / 1000;

/** Position that puts a new item at the top of a column. */
export const getTopPosition = (items: Positioned[]): number =>
  getPositionAt(sortByPosition(items), 0);

/** Position that puts a new item at the bottom of a column. */
export const getBottomPosition = (items: Positioned[]): number =>
  getPositionAt(sortByPosition(items), items.length);

/**
 * Computes the new position of `movedId` when it is dropped at
 * `destinationIndex` of `destinationItems` (the destination column as
 * displayed, possibly still containing the moved item).
 */
export const getDropPosition = (
  destinationItems: Positioned[],
  movedId: Positioned["id"],
  destinationIndex: number,
): number =>
  getPositionAt(
    sortByPosition(destinationItems).filter((item) => item.id !== movedId),
    destinationIndex,
  );
